import { type ReactElement, useId } from "react";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { OnboardingAnswers } from "../../../types/onboarding";
import styles from "./FertilityRoleQuestion.module.css";

interface FertilityRoleQuestionProps {
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | number | string[]) => void;
	showValidation?: boolean;
	// Canonical ENUM values (e.g. "surrogate") in display order. When omitted,
	// the standard four roles render for back-compat.
	visibleRoles?: string[];
}

// Icon components
const StarIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<path
			d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</svg>
);

const UserPlusIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<path
			d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<circle
			cx="8.5"
			cy="7"
			r="4"
			stroke="currentColor"
			strokeWidth="2"
			fill="none"
		/>
		<line
			x1="20"
			y1="8"
			x2="20"
			y2="14"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
		/>
		<line
			x1="23"
			y1="11"
			x2="17"
			y2="11"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
		/>
	</svg>
);

const LifeBuoyIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<circle
			cx="12"
			cy="12"
			r="10"
			stroke="currentColor"
			strokeWidth="2"
			fill="none"
		/>
		<circle
			cx="12"
			cy="12"
			r="4"
			stroke="currentColor"
			strokeWidth="2"
			fill="none"
		/>
		<line
			x1="4.93"
			y1="4.93"
			x2="9.17"
			y2="9.17"
			stroke="currentColor"
			strokeWidth="2"
		/>
		<line
			x1="14.83"
			y1="14.83"
			x2="19.07"
			y2="19.07"
			stroke="currentColor"
			strokeWidth="2"
		/>
		<line
			x1="14.83"
			y1="9.17"
			x2="19.07"
			y2="4.93"
			stroke="currentColor"
			strokeWidth="2"
		/>
		<line
			x1="4.93"
			y1="19.07"
			x2="9.17"
			y2="14.83"
			stroke="currentColor"
			strokeWidth="2"
		/>
	</svg>
);

const EyeIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<path
			d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
		<circle
			cx="12"
			cy="12"
			r="3"
			stroke="currentColor"
			strokeWidth="2"
			fill="none"
		/>
	</svg>
);

const HeartIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<path
			d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</svg>
);

const HomeIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<path
			d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
		<path
			d="M9 22V12h6v10"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</svg>
);

const UsersIcon = () => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<path
			d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<circle
			cx="9"
			cy="7"
			r="4"
			stroke="currentColor"
			strokeWidth="2"
			fill="none"
		/>
		<path
			d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

// Option definition keyed by canonical ENUM value. `value` is the LABEL string
// the wizard persists and onboardingVocabulary maps back to the enum.
interface RoleOptionDef {
	value: string;
	labelKey: string;
	icon: ReactElement;
}

const ROLE_OPTION_BY_ENUM: Record<string, RoleOptionDef> = {
	primary_patient: {
		value: "Primary patient",
		labelKey: "fertilityRole.options.primaryPatient",
		icon: <StarIcon />,
	},
	partner: {
		value: "Partner",
		labelKey: "fertilityRole.options.partner",
		icon: <UserPlusIcon />,
	},
	support_person: {
		value: "Support person",
		labelKey: "fertilityRole.options.supportPerson",
		icon: <LifeBuoyIcon />,
	},
	exploring_options: {
		value: "Exploring my options",
		labelKey: "fertilityRole.options.exploring",
		icon: <EyeIcon />,
	},
	surrogate: {
		value: "Surrogate",
		labelKey: "fertilityRole.options.surrogate",
		icon: <HeartIcon />,
	},
	intended_parent: {
		value: "Intended parent",
		labelKey: "fertilityRole.options.intendedParent",
		icon: <HomeIcon />,
	},
	parent: {
		value: "Parent",
		labelKey: "fertilityRole.options.parent",
		icon: <UsersIcon />,
	},
};

// The standard four roles, used when no per-status list is supplied.
const DEFAULT_ROLE_ENUMS = [
	"primary_patient",
	"partner",
	"support_person",
	"exploring_options",
];

export function FertilityRoleQuestion({
	answers,
	onInputChange,
	showValidation = false,
	visibleRoles,
}: FertilityRoleQuestionProps) {
	const { t, direction } = useClientTranslations("onboarding");
	const { logOnboarding } = useAnalytics();
	const groupId = useId();

	const handleRoleSelect = (role: string) => {
		onInputChange("fertilityRole", role);
		logOnboarding("role_selected", {
			field_name: "fertilityRole",
			selected_value: role,
		});
	};

	const roleHasError = showValidation && !answers.fertilityRole;

	const orderedEnums =
		visibleRoles && visibleRoles.length > 0 ? visibleRoles : DEFAULT_ROLE_ENUMS;
	const roleOptions = orderedEnums
		.map((roleEnum) => ROLE_OPTION_BY_ENUM[roleEnum])
		.filter((def): def is RoleOptionDef => def !== undefined)
		.map((def) => ({
			value: def.value,
			label: t(def.labelKey),
			icon: def.icon,
		}));

	return (
		<div className={styles.container} dir={direction}>
			{/* biome-ignore lint/a11y/useSemanticElements: Using div with role="group" to maintain consistent styling with other components */}
			<div className={styles.optionsList} role="group" id={groupId}>
				{roleOptions.map((option) => (
					<button
						key={option.value}
						type="button"
						onClick={() => handleRoleSelect(option.value)}
						className={`${styles.optionCard} ${
							answers.fertilityRole === option.value
								? styles.optionCardSelected
								: ""
						} ${roleHasError ? styles.optionCardError : ""}`}
					>
						<div className={styles.iconContainer}>{option.icon}</div>
						<span className={styles.optionLabel}>{option.label}</span>
					</button>
				))}
			</div>
		</div>
	);
}
